/* eslint-disable no-nested-ternary */
import React from 'react';
import { classNames } from '../../../../utils';
import { ModelGradientProps } from '../ModelGradient.types';

export function BusinessContentGradient({
    colors,
    rotate,
    animate,
    scale,
    borderRadius,
}: Omit<ModelGradientProps, 'gradient'>) {
    const [mainColor, secondaryColor, lightColor] = colors;
    let scaleValue = 1;
    if (scale && scale !== 'none') {
        scaleValue = {
            s: 1.1,
            m: 1.2,
            l: 1.3,
            xl: 1.6,
        }[scale];
    }

    const gradientWrapperStyle = {
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderRadius,
        ['--main-color' as string]: mainColor,
        ['--secondary-color' as string]: secondaryColor,
        ['--light-color' as string]: lightColor ?? '#cdcdcd',
        ['--sphere-light-color-stop' as string]: '0%',
        ['--sphere-secondary-color-stop' as string]: '85%',
        position: 'relative' as const,
        background: `linear-gradient(0deg, var(--secondary-color) 0%, var(--main-color) 50%, var(--light-color) 100%)`,
    };

    // const shouldStretchGradient = rotate === 90 || rotate === 270;
    const gradientStyle = {
        ['--rotate' as string]: rotate,
        ['--scale' as string]: scaleValue,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column' as const,
        padding: 0,
        transformOrigin: 'center',
        scale: `calc(var(--scale, 1))`,
        rotate: `calc(var(--rotate) * 1deg)`,
        position: 'relative' as const,
    };

    const getRotatedSphereStyles = () => {
        if (rotate === 90 || rotate === 270) {
            return {
                top: { left: '-7.5%', top: '60%' },
                bottom: { right: '-7.5%', bottom: '60%' },
            };
        }

        return {
            top: { top: '-50%', left: '-7.5%' },
            bottom: { bottom: '-55%', left: '-7.5%' },
        };
    };

    const spherePositions = getRotatedSphereStyles();

    const topSphereStyle = {
        position: 'absolute' as const,
        ...spherePositions.top,
        width: '115%',
        height: '100%',
        background: `radial-gradient(49.65% 49.85% at 50% 50%, var(--light-color) var(--sphere-light-color-stop), var(--secondary-color) var(--sphere-secondary-color-stop), var(--main-color) 100%)`,
        borderRadius: '50%',
    };

    const bottomSphereStyle = {
        position: 'absolute' as const,
        ...spherePositions.bottom,
        width: '115%',
        height: '100%',
        background: `radial-gradient(49.65% 49.85% at 50% 50%, var(--light-color) var(--sphere-light-color-stop), var(--secondary-color) var(--sphere-secondary-color-stop), var(--main-color) 100%)`,
        borderRadius: '50%',
    };

    return (
        <div style={gradientWrapperStyle}>
            <div
                style={gradientStyle}
                className={classNames(
                    animate === 'hover' && 'music-model-gradient-animate-hover',
                    animate === 'always' && 'music-model-gradient-animate-always'
                )}
            >
                <div style={topSphereStyle} />
                <div style={bottomSphereStyle} />
            </div>
            <style>
                {`
                    @keyframes floatTop {
                        0% { transform: translateY(0); }
                        50% { transform: translateY(-5%); }
                        100% { transform: translateY(0); }
                    }

                    @keyframes floatBottom {
                        0% { transform: translateY(0); }
                        50% { transform: translateY(5%); }
                        100% { transform: translateY(0); }
                    }

                    .music-model-gradient-animate-hover {
                        transition: all 0.6s cubic-bezier(0.17, 0.67, 0.89, 0.58);
                    }

                    .music-model-gradient-animate-hover div:first-child {
                        animation: floatTop 6s ease-in-out infinite;
                        animation-play-state: paused;
                    }

                    .music-model-gradient-animate-hover div:last-child {
                        animation: floatBottom 6s ease-in-out infinite;
                        animation-play-state: paused;
                    }

                    .music-model-gradient-animate-hover:hover {
                        cursor: pointer;
                    }

                    .music-model-gradient-animate-hover:hover div:first-child {
                        animation-play-state: running;
                    }

                    .music-model-gradient-animate-hover:hover div:last-child {
                        animation-play-state: running;
                    }

                    .music-model-gradient-animate-always div:first-child {
                        animation: floatTop 6s ease-in-out infinite;
                        animation-play-state: running;
                    }

                    .music-model-gradient-animate-always div:last-child {
                        animation: floatBottom 6s ease-in-out infinite;
                        animation-play-state: running;
                    }
                `}
            </style>
        </div>
    );
}
